import axios, { isAxiosError } from "axios"
import { OpaqueSparqlResults } from "./sparqlResultHelpers"
import { freezeInDevMode } from '@/utils/dev-mode'
import { registerEventLog } from '@/components/editor/event-logs/event-log-store'
import { formatTimestamp } from '@/utils/format-timestamp'
import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { store } from '@/store'
import { ViewerConfig } from '@/components/Viewer'
import { queryClient } from '@/queryClient'
import { ComponentNamespace, useNamespace } from '@/redux/pubsub'

export type SparqlStatus = "success" | "pending" | "error" | "disabled"

type SparqlQueryOptions<T, TData>
  = Partial<Omit<UseQueryOptions<T, Error, TData>, "queryKey" | "queryFn" | "retry">>
  & { maxRetries?: number }

export type QueryRequest<T = OpaqueSparqlResults, TData = T> = {
  /** The query */
  query: string

  /** The endpoint to fetch with */
  sparqlEndpoint: string

  /** Optional source name for logging */
  source?: string

  /** Component namespace this request originates from (used for event-log attribution). */
  namespace?: ComponentNamespace
} & SparqlQueryOptions<T, TData>

export class ErrorNeedAuth extends Error {}
export class SparqlError extends Error {}

type FetchingFn<T> = {
  (
    request: Pick<QueryRequest, "query" | "sparqlEndpoint" | "source" | "namespace">,
    abortSignal: AbortSignal,
    isRefetch: boolean
  ): Promise<T>
  type: "sparql" | "construct"
}

function createUseQueryOptions<T, TData = T>(
  fetchingFn: FetchingFn<T>,
  request: QueryRequest<T, TData>
): UseQueryOptions<T, Error, TData> {
  const { query, sparqlEndpoint, ...queryOptions } = request
  const shouldAlwaysRefetch = shouldAlwaysRefetchQuery(query)
  const viewerConfig = store.getState()?.currentViewer?.config as ViewerConfig | undefined
  const { refetchOnWindowFocus, refetchOnReconnect } = viewerConfig ?? {}
  const hasRefetchTriggers = Boolean(refetchOnWindowFocus || refetchOnReconnect)

  return {
    queryKey: ['sparql-manager', { sparqlEndpoint }, query ], //todokey
    queryFn: ({ signal, queryKey }) => {
      const isRefetch = queryClient.getQueryCache().find({ queryKey })?.state.status === 'success'
      return fetchingFn(request, signal, isRefetch)
    },
    refetchOnMount: shouldAlwaysRefetch,
    gcTime: shouldAlwaysRefetch ? 0 : Infinity,
    staleTime: (shouldAlwaysRefetch || hasRefetchTriggers) ? 0 : Infinity,
    retry: (failureCount: number, error: Error) => {
      if (error instanceof ErrorNeedAuth) return false
      if (error instanceof SparqlError) return false
      return failureCount <= (request.maxRetries ?? 1)
    },
    refetchOnWindowFocus: refetchOnWindowFocus ?? false,
    refetchOnReconnect: refetchOnReconnect ?? false,
    ...queryOptions
  }
}

export function createUseSparqlQueryOptions<TData = OpaqueSparqlResults>(request: QueryRequest<OpaqueSparqlResults, TData>) {
  return createUseQueryOptions(fetchSelect, request)
}

export function createUseConstructQueryOptions<TData = string>(request: QueryRequest<string, TData>) {
  return createUseQueryOptions(fetchConstruct, request)
}

export function useSparqlQuery<TData = OpaqueSparqlResults>(
  request: QueryRequest<OpaqueSparqlResults, TData>
) {
  const namespace = useNamespace()
  return useQuery(createUseSparqlQueryOptions({ namespace, ...request }))
}

export function useConstructQuery<TData = string>(
  request: QueryRequest<string, TData>
) {
  const namespace = useNamespace()
  return useQuery(createUseConstructQueryOptions({ namespace, ...request }))
}

async function fetchSelect(
  request: Pick<QueryRequest, "query" | "sparqlEndpoint" | "source" | "namespace">,
  abortSignal: AbortSignal,
  isRefetch: boolean
) {
  // I expect some mutation shenanigans, let us error them in dev mode
  return freezeInDevMode(await fetchLD({
    type: "sparql",
    request,
    abortSignal,
    verify: result => {
      if (!result) {
        console.error(`SPARQL query did not return valid results: most likely the query contains an error`, request.query)
        throw new SparqlError(`SPARQL query did not return valid results: most likely the query contains an error`)
      }
      if (typeof result !== "object") throw new Error("SPARQL endpoint did not return a JSON")
      return result as OpaqueSparqlResults
    },
    log: resultOrError => {
      const responseString = (() => {
        if (resultOrError == null) return ""
        if (resultOrError instanceof Error) return resultOrError.message
        if (typeof resultOrError === "object") return JSON.stringify(resultOrError, null, 2)
        return String(resultOrError)
      })()
      registerEventLog({
        type: 'sparql-request-event',
        request: request.query,
        endpoint: request.sparqlEndpoint,
        source: request.source ?? "unknown",
        namespace: request.namespace,
        isRefetch,
        response: responseString,
        timestamp: formatTimestamp(new Date()),
      })
    }
  }))
}
fetchSelect.type = "sparql" as const

async function fetchConstruct(
  request: Pick<QueryRequest, "query" | "sparqlEndpoint" | "source" | "namespace">,
  abortSignal: AbortSignal,
  isRefetch: boolean
): Promise<string> {
  return await fetchLD({
    type: "construct",
    request,
    abortSignal,
    verify: result => {
      if (!result) {
        console.error(`CONSTRUCT query did not return valid results: most likely the query contains an error`, request.query)
        throw new SparqlError(`CONSTRUCT query did not return valid results: most likely the query contains an error`)
      }
      if (typeof result !== "string") throw new Error("CONSTRUCT endpoint did not return a string")
      return result
    },
    log: resultOrError => {
      const responseString = (() => {
        if (resultOrError == null) return ""
        if (resultOrError instanceof Error) return resultOrError.message
        return String(resultOrError)
      })()
      registerEventLog({
        type: 'construct-request-event',
        request: request.query,
        endpoint: request.sparqlEndpoint,
        source: request.source ?? "unknown",
        namespace: request.namespace,
        isRefetch,
        response: responseString,
        timestamp: formatTimestamp(new Date()),
      })
    }
  })
}
fetchConstruct.type = "construct" as const

async function fetchLD<T>(args: {
  type: "sparql" | "construct",
  request: Pick<QueryRequest, "query" | "sparqlEndpoint" | "source" | "namespace">,
  abortSignal: AbortSignal,
  verify: (result: unknown) => T,
  log: (resultOrError: unknown) => void
}): Promise<T> {
  try {
    const data = new URLSearchParams()
    data.set('infer', String(false))
    data.set('sameAs', String(false))
    data.set('query', args.request.query)

    const response = await axios({
      method: 'post',
      url: args.request.sparqlEndpoint,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      data,
      // @ts-expect-error Hans hack
      withCredentials: document.withCredentials ?? true,
      headers: {
        Accept: args.type === "sparql"
          ? 'application/sparql-results+json,*/*;q=0.9'
          : 'text/turtle,application/n-triples,*/*;q=0.9',
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      },
      signal: args.abortSignal
    })

    const match = args.request.query.match(/#\s*artificially-slow-query\s*=\s*([0-9]+)/)
    if (match) {
      await waitFor(parseInt(match[1]))
    }

    const result = args.verify(response.data)
    args.log(result)
    return result
  } catch (error) {
    args.log(error)
    if (isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403))
      throw new ErrorNeedAuth()
    throw error
  }
}

async function waitFor(ms: number): Promise<void> {
  return new Promise(resolve => {
    setTimeout(() => resolve(), ms)
  })
}

function shouldAlwaysRefetchQuery(query: string) {
  return query.includes("#nocache")
}
